import * as React from 'react';
import { PassengerFragment, SubsidyInfoFragment } from '@websky/graphql';
import { useFormState } from 'react-final-form';
import { validate } from '../../../../../../Passenger/utils';
import { getInputType } from '../Passengers';
import { findSubsidyForPassenger } from '../../SubsidyRequest/utils';
import SubsidyRejectPopup from '../../../../../../SubsidyRejectPopup/SubsidyRejectPopup';
import { OrderSubsidyState, SubsidyResult } from '../../SubsidyRequest/subsidyHook';
import { useTranslation } from 'react-i18next';

export interface PassengerSubsidyChildrenProps {
	withSubsidy?: boolean;
	noSubsidyAvailable?: boolean;
	subsidyRejectMessage?: string;
}

export enum SubsidyState {
	None,
	Checking,
	Confirmed,
	NoAvailable
}

interface Props {
	passengerId: string;
	subsidyInfo: SubsidyInfoFragment[];
	fields: PassengerFragment['values'];
	children: (props: PassengerSubsidyChildrenProps) => React.ReactNode;
	orderSubsidyState?: OrderSubsidyState;
	backToResults?: () => void;
}

const PassengerSubsidy: React.FC<Props> = ({
	passengerId,
	subsidyInfo,
	fields,
	children,
	orderSubsidyState,
	backToResults
}) => {
	const formState = useFormState(),
		passengerValues = formState.values['passengers']?.[parseInt(passengerId)];

	const { t } = useTranslation('PassengerSubsidy');

	const [errorPopupOpen, setErrorPopupOpen] = React.useState(false);
	const [subsidyResult, setSubsidyResult] = React.useState<SubsidyResult>();

	const isValidForSubsidyCheck = React.useMemo(() => {
		if (!subsidyInfo?.length) {
			return false;
		} else {
			for (const field of fields) {
				if (
					!!validate(
						passengerValues?.[field.name],
						passengerValues,
						getInputType(field.type),
						field.validationRules
					)
				) {
					return false;
				}
			}

			return true;
		}
	}, [passengerValues, subsidyInfo, fields]);

	React.useEffect(() => {
		if (isValidForSubsidyCheck && orderSubsidyState?.canCheckSubsidies) {
			const subsidyId = findSubsidyForPassenger(passengerValues, subsidyInfo);

			if (subsidyId) {
				orderSubsidyState
					.checkSubsidy({
						id: passengerId,
						subsidyId,
						values: fields
							.filter(field => passengerValues.hasOwnProperty(field.name))
							.map(field => ({
								type: field.type,
								name: field.name,
								value: passengerValues[field.name]
							}))
					})
					.then(data => {
						if (!data.isSubsidyApplied) {
							setErrorPopupOpen(true);
							setSubsidyResult(data);
						}
					});
			}
		}
	}, [isValidForSubsidyCheck, orderSubsidyState?.canCheckSubsidies]);

	const subsidyTravellerState = React.useMemo(() => {
		const subsidyTravellerMap = new Map(
			orderSubsidyState?.travellers?.map(subsidy => [subsidy.traveller.id, subsidy]) ?? []
		);

		return subsidyTravellerMap.get(passengerId);
	}, [orderSubsidyState?.travellers, passengerId]);

	return (
		<>
			{orderSubsidyState?.isSubsidiesAvailable && (
				<SubsidyRejectPopup
					open={errorPopupOpen}
					traveller={subsidyResult?.traveller}
					segment={subsidyResult?.failedSegment}
					rejectMessage={subsidyResult?.rejectMessage ?? t('Subsidies not available')}
					onClose={() => setErrorPopupOpen(false)}
					onNewSearch={backToResults}
				/>
			)}

			{children({
				noSubsidyAvailable: subsidyTravellerState?.isSubsidyRejected,
				withSubsidy: orderSubsidyState?.isSubsidiesAvailable,
				subsidyRejectMessage: subsidyTravellerState?.rejectMessage
					? subsidyTravellerState.rejectMessage
					: t('Subsidies not available')
			})}
		</>
	);
};

export default PassengerSubsidy;
